import styled from "styled-components";

import { Button, FormSize } from "ui/ecosystems/forms";

/**
 * SKE-4716. `reconnect` exists because `connected` renders an inert label, not
 * a button.
 *
 * An Instagram account that has failed a publish with a `needs_reconnect` error
 * carries `instagramPublishReconnectRequired`, stops routing to the API track,
 * and is cleared only by genuinely re-adding the account. It is still "already
 * added", so it was showing as `connected` — a green label with nothing to
 * click — while the accounts page simultaneously said "Publishing: needs
 * update". The accounts page has a per-account reconnect ("Update Facebook
 * permissions"); this picker had none.
 */
type ConnectButtonState =
  | "connected"
  | "reconnect"
  | "reconnectDisabled"
  | "connecting"
  | "disabled"
  | "active";

interface ConnectButtonProps {
  className?: string;
  state: ConnectButtonState;
  onClick(): void;
}

function getLabel(state: ConnectButtonState) {
  switch (state) {
    case "connected":
      return "Connected";
    case "reconnect":
    case "reconnectDisabled":
      return "Reconnect";
    case "connecting":
      return "Connecting...";
    default:
      return "Connect";
  }
}

export type GetConnectButtonState<T> = (item: T) => ConnectButtonState;

interface ConnectButtonPredicates<T> {
  isConnected(i: T): boolean;
  isConnecting(i: T): boolean;
  isDisabled(i: T): boolean;
  /**
   * Optional, so every other connect dialog keeps its existing three-way
   * behaviour untouched. Only checked for an item that is already connected.
   */
  needsReconnect?(i: T): boolean;
}

function getConnectButtonState<T>(
  item: T,
  {
    isConnected,
    isConnecting,
    isDisabled,
    needsReconnect,
  }: ConnectButtonPredicates<T>
): ConnectButtonState {
  /*
   * A caller that supplies `needsReconnect` checks connecting FIRST, so a
   * reconnect in flight reads as "Connecting…" rather than flipping back to an
   * enabled "Reconnect" while the request is open and inviting a second click.
   *
   * Callers that do not supply it keep the original order exactly. That is not
   * cosmetic caution: `isConnected` and `isConnecting` CAN both be true for a
   * moment right after a successful connect, if the added-accounts list updates
   * before the connecting id clears. Under the original order that moment reads
   * "Connected"; reordering it globally would have changed what the five other
   * dialogs that use this factory show in that window (Facebook Page, Facebook
   * Group, YouTube, LinkedIn, Google My Business), for no reason connected to
   * this fix.
   */
  if (needsReconnect && isConnecting(item)) {
    return "connecting";
  } else if (isConnected(item)) {
    if (!needsReconnect?.(item)) {
      return "connected";
    }
    /*
     * `isDisabled` has to be consulted HERE, not left to the branch below,
     * which an already-connected item can never reach.
     *
     * It did not matter before this state existed, because `connected` renders
     * an inert div and an inert div cannot be pressed at the wrong moment. A
     * `reconnect` row renders a real button, and this dialog's `isDisabled` is
     * GLOBAL, true whenever any connect or reconnect is in flight, while
     * `isConnecting` is per-item. Without this, reconnecting account A leaves
     * account B's button live, and the moment B starts, A's button becomes live
     * again while A's request is still open. Two concurrent
     * `PUT /accounts/addIgToken/{id}` calls for one account each replace
     * `session` and `longSession` wholesale, re-register webhooks and reset the
     * token-refresh job.
     */
    return isDisabled(item) ? "reconnectDisabled" : "reconnect";
  } else if (isConnecting(item)) {
    return "connecting";
  } else if (isDisabled(item)) {
    return "disabled";
  } else {
    return "active";
  }
}

export function getConnectButtonStateFactory<T>(
  predicates: ConnectButtonPredicates<T>
): GetConnectButtonState<T> {
  return (item: T) => getConnectButtonState(item, predicates);
}

export function ConnectButton({
  className,
  state,
  onClick,
}: ConnectButtonProps) {
  if (state === "connected") {
    return (
      <ConnectedDiv className={className} as="div">
        {getLabel(state)}
      </ConnectedDiv>
    );
  }

  return (
    <StyledButton
      primary
      size={FormSize.small}
      className={className}
      disabled={["connecting", "disabled", "reconnectDisabled"].some(
        (s) => s === state
      )}
      onClick={() => onClick()}
    >
      {getLabel(state)}
    </StyledButton>
  );
}

const StyledButton = styled(Button)`
  min-width: 100px;
  display: inline-block;
`;

const ConnectedDiv = styled.div`
  width: 100px;
  height: 30px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  line-height: 125.5%;
  font-weight: 500;
  background: #b0ffb0;
  color: green;
  border-radius: 5px;
`;
